import { ColumnApi, GridApi, GridOptions, ISetFilterParams } from '@ag-grid-community/core';
import { Connect, Message } from '@genesislcap/foundation-comms';
import { DatePicker } from '@genesislcap/foundation-ui';
import { customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { IssuerRating } from '../../types';
import { numberFormatter } from '../../utils';
import { getNearestMonday, getNextMonday, getPreviousMonday } from '../../utils/dates';
import { FeatureFlags, Features } from '../../utils/feature';
import {
  BadgeRenderer,
  CounterpartyOrderStatusRenderer,
  OfferingTypeRenderer,
  StructureRenderer,
} from './cell-renderers';
import { usStates } from './deals.data';
import { dealsStyles as styles } from './deals.styles';
import { dealsTemplate as template } from './deals.template';
import { DealStatusEnum, DetailViewDeal, EnumValuePair, FilterPill, TasStatusLabel } from './deals.types';
import {
  checkIsForwardCalendar,
  createFilterPills,
  formatPricingDate,
  formatPricingDateForTooltip,
  getDealsDatasourceCriteria,
  getCombinedIssuerCreditRatingFilterOptions,
  getDisplayDealStatus,
  getFixedWidthConfig,
  formatRatingFilterLabel,
  getIssuerCreditRatingFilterOptions,
  getIssuerRatingLabel,
  getMyCpOrderQtyCellTooltip,
  hasEligibleCounterpartyForCurrentUser,
  reorderNodesNullExpectedPricingDateLast,
  renderSyndicateCell,
  toUTCDateOnly,
  buildSeriesRatingsByDealId,
  SeriesRatingRow,
  type SetFilterUniverses,
} from './deals.util';
import { CombinedRatingFilter } from './combined-rating.filter';
import { DayOfWeekFilter } from './multi-day-filter';
import { getForwardCalendarMonday } from '../../utils/dates';

@customElement({
  name: 'muni-deals',
  template,
  styles,
})
export class MuniDeals extends GenesisElement {
  @Connect connect!: Connect;
  @FeatureFlags featureFlags: FeatureFlags;

  dateCalendar: HTMLElement;

  private gridApi: GridApi;

  private colApi: ColumnApi;

  /** Prevents onSortChanged re-entrancy when we programmatically restore fixed sorts. */
  private applyingFixedSorts = false;

  private enumValueMapping: EnumValuePair[];

  private enumsByColumn: Map<string, EnumValuePair[]> = new Map();

  /** Last option lists from async set-filter loaders — used for concise filter pills. */
  private sectorFilterUniverse: string[] = [];

  private leadFilterUniverse: string[] = [];


  @observable weekPicker!: DatePicker;

  @observable filterPills: FilterPill[];

  @observable forwardCalendar: boolean = false;

  @observable dealsDate = getNearestMonday();

  /** Deal IDs that are currently open in detail tabs (from route). Included in dataserver criteria for real-time updates. */
  @observable openDetailViewDealIds: (string | number)[] = [];

  @observable criteria: string = getDealsDatasourceCriteria(this.dealsDate, this.forwardCalendar, this.openDetailViewDealIds);

  dealsDatasource: { reloadResourceData?: () => Promise<void> };

  @observable dealsDateCalendar: boolean = false;

  private issuerCreditRatings: IssuerRating;

  private seriesRatingsByDealId: Map<string, SeriesRatingRow[]> = new Map();

  private seriesRatingsSubscription: { unsubscribe(): void } | null = null;

  @observable gridOptions: GridOptions = {
    /** Required for `tooltipValueGetter` without registering `@ag-grid-enterprise/tooltips`. */
    enableBrowserTooltips: true,
    tooltipShowDelay: 400,
    suppressMovableColumns: true,
    suppressContextMenu: true,
    suppressMultiSort: false,
    getRowId: (params) => String(params.data?.ROW_REF ?? params.data?.DEAL_ID ?? ''),
    // After column sort, keep day-to-day deals (null EXPECTED_PRICING_DATE) at the bottom.
    postSortRows: (params) => {
      if (params.nodes?.length) {
        reorderNodesNullExpectedPricingDateLast(params.nodes);
      }
    },
    // Date and Sale Type are permanent sort keys — they must always be present in the
    // multi-sort. A plain click on any single column replaces the entire AG Grid sort
    // model, silently clearing the others. Restore whichever ones were cleared while
    // keeping the direction the user chose for the column they actually clicked.
    onSortChanged: (event) => {
      if (this.applyingFixedSorts) return;
      const state = event.columnApi.getColumnState();
      const dateCol = state.find(c => c.colId === 'EXPECTED_PRICING_DATE');
      const typeCol = state.find(c => c.colId === 'DEAL_TYPE');

      // Both already present — nothing to restore.
      if (dateCol?.sort != null && typeCol?.sort != null) return;

      const parSort = state.find(c => c.colId === 'DEAL_SIZE')?.sort ?? 'desc';
      this.applyingFixedSorts = true;
      event.columnApi.applyColumnState({
        state: [
          { colId: 'EXPECTED_PRICING_DATE', sort: dateCol?.sort ?? 'asc', sortIndex: 0 },
          { colId: 'DEAL_TYPE', sort: typeCol?.sort ?? 'asc', sortIndex: 1 },
          { colId: 'DEAL_SIZE', sort: parSort, sortIndex: 2 },
        ],
      });
      this.applyingFixedSorts = false;
    },
    columnDefs: [
      {
        field: 'DEAL_TYPE',
        headerName: '',
        ...getFixedWidthConfig(40),
        cellRenderer: 'offeringTypeRenderer',
        // filter: false,
        filterParams: {
          values: (params) => {
            const mappings = this.getDealTypeFilterValues().map(e => e.COLUMN_VALUE);
            params.success(mappings);
          },
          valueFormatter: (params) => params.value
        },
        sort: 'asc',
        sortIndex: 1,
      },
      {
        field: 'EXPECTED_PRICING_DATE',
        headerName: 'Date',
        ...getFixedWidthConfig(90),
        filter: DayOfWeekFilter,
        valueFormatter: (params) => formatPricingDate(params.value, this.forwardCalendar),
        tooltipValueGetter: (params) => {
          return formatPricingDateForTooltip(params.value);
        },
        // Compare only the UTC calendar date so that time-of-day differences in
        // the timestamp don't affect sort order.
        comparator: (a: number | null, b: number | null) => {
          if (a == null && b == null) return 0;
          if (a == null) return 1;
          if (b == null) return -1;
          return toUTCDateOnly(a) - toUTCDateOnly(b);
        },
        sort: 'asc',
        sortIndex: 0,
      },
      {
        field: 'DEAL_SIZE',
        headerName: 'Par ($M)',
        ...getFixedWidthConfig(120),
        filter: 'agNumberColumnFilter',
        cellStyle: {
          textAlign: 'right',
        },
        valueGetter: (params) => {
          return params.data['DEAL_SIZE'] / 1000;
        },
        valueFormatter: numberFormatter(0),
        sort: 'desc',
        sortIndex: 2,
        // Prevent cycling to "no sort" so Par always remains a sort key.
        // Default AG Grid cycle is asc → desc → null; starting from 'desc' a
        // single click would land on null and silently drop Par from the multi-
        // sort, leaving Competitive deals in unpredictable server order.
        sortingOrder: ['desc', 'asc'],
      },
      {
        field: 'DEAL_DESCRIPTION',
        headerName: 'Issue Description',
        minWidth: 120,
        width: 420,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        tooltipValueGetter: (params) => params.value,
        suppressMenu: false,
      },
      {
        field: 'STATE',
        headerName: 'State',
        ...getFixedWidthConfig(60),
        filterParams: {
          values: usStates.map((s) => s.code),
          valueFormatter: (params) => {
            const state = usStates.find((s) => s.code === params.value);
            return `${state.name} (${state.code})`;
          },
        },
      },
      {
        field: 'TAX_STATUS',
        headerName: 'Tax Status',
        ...getFixedWidthConfig(80),
        valueGetter: (params) => TasStatusLabel[params.data?.TAX_STATUS] ?? '',
        // filter: false,
        filterParams: {
          values: (params) => {
            const mappings = this.getTaxStatusFilterValues().map((e) => TasStatusLabel[e.COLUMN_VALUE] ?? '');
            params.success(mappings);
          },
          valueFormatter: (params) => params.value
        }
      },
      {
        field: 'creditRating',
        headerName: 'Rating',
        headerClass: 'group-border',
        children: [
          {
            columnGroupShow: 'closed',
            headerName: 'M/S/F/K',
            field: 'ISSUER_RATING',
            ...getFixedWidthConfig(180),
            valueFormatter: (params) => {
              return `${this.formatRatingForCell(params.data.DEAL_MOODYS_RATING)} / ${this.formatRatingForCell(params.data.DEAL_SP_RATING)} / ${this.formatRatingForCell(params.data.DEAL_FITCH_RATING)} / ${this.formatRatingForCell(params.data.DEAL_KROLL_RATING)}`;
            },
            filter: CombinedRatingFilter,
            filterParams: {
              getIssuerRatings: () => this.issuerCreditRatings,
              getSeriesByDealId: () => this.seriesRatingsByDealId,
            },
          },
          {
            columnGroupShow: 'open',
            field: 'DEAL_MOODYS_RATING',
            headerName: `Moody's`,
            ...getFixedWidthConfig(65),
            valueFormatter: (params) => this.formatRatingForCell(params.value),
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params) => {
                const ratings = getIssuerCreditRatingFilterOptions(this.issuerCreditRatings.MOODYS);
                params.success([...ratings]);
              },
              keyCreator: (params) => {
                return getIssuerRatingLabel(this.issuerCreditRatings.MOODYS, params.value);
              },
              valueFormatter: (params) => this.formatRatingForFilter(params.value),
              suppressSorting: true, // prevents AG Grid from auto-sorting groups
            },
          },
          {
            columnGroupShow: 'open',
            field: 'DEAL_SP_RATING',
            headerName: 'S&P',
            ...getFixedWidthConfig(65),
            valueFormatter: (params) => this.formatRatingForCell(params.value),
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params) => {
                const ratings = getIssuerCreditRatingFilterOptions(this.issuerCreditRatings.SANDP);
                params.success([...ratings]);
              },
              keyCreator: (params) => {
                return getIssuerRatingLabel(this.issuerCreditRatings.SANDP, params.value);
              },
              valueFormatter: (params) => this.formatRatingForFilter(params.value),
              suppressSorting: true, // prevents AG Grid from auto-sorting groups
            },
          },
          {
            columnGroupShow: 'open',
            field: 'DEAL_FITCH_RATING',
            headerName: 'Fitch',
            ...getFixedWidthConfig(65),
            valueFormatter: (params) => this.formatRatingForCell(params.value),
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params) => {
                const ratings = getIssuerCreditRatingFilterOptions(this.issuerCreditRatings.FITCH);
                params.success([...ratings]);
              },
              keyCreator: (params) => {
                return getIssuerRatingLabel(this.issuerCreditRatings.FITCH, params.value);
              },
              valueFormatter: (params) => this.formatRatingForFilter(params.value),
              suppressSorting: true, // prevents AG Grid from auto-sorting groups
            },
          },
          {
            columnGroupShow: 'open',
            field: 'DEAL_KROLL_RATING',
            headerName: 'Kroll',
            ...getFixedWidthConfig(65),
            valueFormatter: (params) => this.formatRatingForCell(params.value),
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params) => {
                const ratings = getIssuerCreditRatingFilterOptions(this.issuerCreditRatings.KROLL);
                params.success([...ratings]);
              },
              keyCreator: (params) => {
                return getIssuerRatingLabel(this.issuerCreditRatings.KROLL, params.value);
              },
              valueFormatter: (params) => this.formatRatingForFilter(params.value),
              suppressSorting: true, // prevents AG Grid from auto-sorting groups
            },
          },
        ],
      },
      {
        field: 'DEAL_SECTOR',
        headerName: 'Sector',
        ...getFixedWidthConfig(120),
        filter: 'agSetColumnFilter',
        filterParams: this.getSectorFilterParams(),
      },
      {
        field: 'SHORT_NAME',
        headerName: 'Lead',
        cellRenderer: renderSyndicateCell,
        filter: 'agSetColumnFilter',
        filterParams: this.getLeadShortNameFilterParams(),
        valueGetter: (params) => {
          // Return the displayed value for filtering: SHORT_NAME if present, otherwise first syndicate value
          const shortName = params.data?.SHORT_NAME;
          if (shortName) {
            return shortName;
          }
          const syndicate = params.data?.SYNDICATE;
          return syndicate?.split(';')?.[0]?.trim() || '';
        },
      },
      {
        colId: 'MY_COUNTERPARTY_ORDER_QTY',
        field: 'SUBMITTED_QTY',
        headerName: 'Orders ($M)',
        ...getFixedWidthConfig(168),
        filter: false,
        floatingFilter: false,
        cellRenderer: 'counterpartyOrderStatusRenderer',
        tooltipValueGetter: (params) =>
          getMyCpOrderQtyCellTooltip(
            params.data?.SUBMITTED_QTY,
            params.data?.DRAFT_QTY,
            params.data?.NON_SUCCESS_ORDER_COUNT,
          ),
        sortable: false,
      },
      {
        field: 'DEAL_STATUS',
        headerName: 'Deal Status',
        ...getFixedWidthConfig(290),
        cellRenderer: 'badgeRenderer',
        valueGetter: (params) => {
          const originalStatus = params.data.DEAL_STATUS !== DealStatusEnum.NONE ? params.data.DEAL_STATUS : '';
          if (!originalStatus) return '';
          
          const dealType = params.data.DEAL_TYPE;
          const tradeDate = params.data.DEAL_TRADE_DATE;
          
          // Convert tradeDate to ISO string format to match deal detail implementation
          const tradeDateISO = tradeDate ? new Date(tradeDate).toISOString() : null;
          
          return getDisplayDealStatus(originalStatus, dealType, tradeDateISO);
        },
        cellRendererParams: (params) => {
          if (!params.value) {
            return {};
          }

          return {
            badgeText: params.value,
            badgeFillColor: 'rgba(135, 155, 166, 0.2)',
            justifyContent: 'flex-end',
            orderPeriodBeginDate: params.data?.ORDER_PERIOD_BEGIN_DATE,
            orderPeriodEndDate: params.data?.ORDER_PERIOD_END_DATE,
            dealStatusForOrderPeriod: params.data?.DEAL_STATUS,
          };
        },
        filterParams: {
          values: (params) => {
            const mappings = this.getDealStatusFilterValues().map(e => e.COLUMN_VALUE);
            const computedStatuses = [DealStatusEnum.PRICED, DealStatusEnum.SOLD];
            // Combine enum values with computed values, removing duplicates
            const allValues = [...new Set([...mappings, ...computedStatuses])];
            params.success(allValues);
          },
          valueFormatter: (params) => params.value
        }
      },
    ],
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: false,
      floatingFilter: true,
      menuTabs: [],
    },
    rowHeight: 40,
    getRowClass: (params) => {
      const dealStatus = params.data?.DEAL_STATUS;
      const counterpartyEligibility = params.data?.COUNTERPARTY_ELIGIBILITY;
      const hasEligibleCounterparty = hasEligibleCounterpartyForCurrentUser(
        counterpartyEligibility,
      );
      const shouldHighlight =
        dealStatus === DealStatusEnum.VERBAL_AWARD_CXL_WINDOW && hasEligibleCounterparty;

      if (
        shouldHighlight
      ) {
        return 'deal-row-reconfirm-window';
      }
      return undefined;
    },
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: (node) => {
      const data = node.data;
      if (!data) return false;
      
      const dealId = String(data.DEAL_ID);
      const isOpenTabDeal = this.openDetailViewDealIds?.some((id) => String(id) === dealId);
      
      // If it's not an open-tab deal, show it (it's from the week filter)
      if (!isOpenTabDeal) {
        return true;
      }
      
      // If it's an open-tab deal, only show if it belongs to the current week
      return this.isDealInCurrentWeek(data);
    },
    components: {
      structureRenderer: StructureRenderer,
      offeringTypeRenderer: OfferingTypeRenderer,
      badgeRenderer: BadgeRenderer,
      counterpartyOrderStatusRenderer: CounterpartyOrderStatusRenderer,
    },
    onFilterChanged: () => {
      this.filterPills = createFilterPills(this.gridApi.getFilterModel(), this.buildSetFilterUniverses());
      // Force DEAL_STATUS cells to refresh so badge shows latest data when datasource pushes updates
      this.refreshDealStatusCells();
    },
    onModelUpdated: () => {
      if (!this.gridApi) {
        return;
      }
      requestAnimationFrame(() => {
        this.refreshDealStatusCells();
        this.gridApi.refreshCells({
          columns: ['MY_COUNTERPARTY_ORDER_QTY'],
          force: true,
        });
        this.pushListingUpdatesToOpenTabs();
      });
    },
  };

  private gridResizeTimeoutRef;

  private onResizeBound: (() => void) | null = null;

  private handleDocumentClickBound: ((e) => void) | null = null;

  private formatRatingForCell(value?: string, empty: string = '-'): string {
    if (!value) {
      return empty;
    }
    return value === 'Various' ? 'VAR' : value;
  }

  private formatRatingForFilter(value?: string): string {
    return formatRatingFilterLabel(value ?? '');
  }

  /**
   * When the Data Server pushes an update, emit all open-tab deals to the route.
   * Uses forEachNode so we get ALL subscribed deals including those filtered out (other-week open tabs).
   */
  private pushListingUpdatesToOpenTabs(): void {
    if (!this.openDetailViewDealIds?.length || !this.gridApi) {
      return;
    }

    const deals: DetailViewDeal[] = [];

    this.gridApi.forEachNode((node) => {
      if (!node.data) return;
      const dealId = String(node.data.DEAL_ID);
      const isOpenTabDeal = this.openDetailViewDealIds.some((id) => String(id) === dealId);
      if (isOpenTabDeal) {
        deals.push(node.data);
      }
    });

    if (deals.length) {
      this.$emit('deal-updated-from-listing', { deals });
    }
  }

  private refreshDealStatusCells(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.refreshCells({ columns: ['DEAL_STATUS'], force: true });
    this.gridApi.redrawRows();
  }

  /** Option universes for set-filter pills (concise “Excluded: …” when most values are selected). */
  private buildSetFilterUniverses(): SetFilterUniverses {
    const issuer = this.issuerCreditRatings;
    const universes: SetFilterUniverses = {
      DEAL_TYPE: this.getDealTypeFilterValues().map((e) => e.COLUMN_VALUE),
      STATE: usStates.map((s) => s.code),
      TAX_STATUS: this.getTaxStatusFilterValues().map((e) => TasStatusLabel[e.COLUMN_VALUE] ?? ''),
      DEAL_STATUS: [
        ...new Set([
          ...this.getDealStatusFilterValues().map((e) => e.COLUMN_VALUE),
          DealStatusEnum.PRICED,
          DealStatusEnum.SOLD,
        ]),
      ],
      DEAL_SECTOR: this.sectorFilterUniverse,
      SHORT_NAME: this.leadFilterUniverse,
    };
    if (issuer) {
      universes.ISSUER_RATING = getCombinedIssuerCreditRatingFilterOptions(issuer);
      universes.DEAL_MOODYS_RATING = getIssuerCreditRatingFilterOptions(issuer.MOODYS);
      universes.DEAL_SP_RATING = getIssuerCreditRatingFilterOptions(issuer.SANDP);
      universes.DEAL_FITCH_RATING = getIssuerCreditRatingFilterOptions(issuer.FITCH);
      universes.DEAL_KROLL_RATING = getIssuerCreditRatingFilterOptions(issuer.KROLL);
    }
    return universes;
  }

  private groupEnumsByColumn(enums: EnumValuePair[]): Map<string, EnumValuePair[]> {
    const map = new Map<string, EnumValuePair[]>();
    for (const e of enums) {
      let group = map.get(e.COLUMN_NAME);
      if (!group) {
        group = [];
        map.set(e.COLUMN_NAME, group);
      }
      group.push(e);
    }
    return map;
  }

  private getDealTypeFilterValues() {
    return this.enumsByColumn.get('DEAL_TYPE') ?? [];
  }

  private getTaxStatusFilterValues() {
    return this.enumsByColumn.get('TAX_STATUS') ?? [];
  }

  private getSectorFilterParams(): ISetFilterParams {
    return {
      values: async (params) => {
        try {
          const result = await this.connect.request('REQ_MUNI_PURPOSE_SECTOR_MAPPINGS', {});
          if (result.REPLY.includes('MSG_NACK')) {
            this.sectorFilterUniverse = [];
            params.success([]);
            return;
          }
          const allSectors = (result.REPLY || []).map((r) => r['MAPPED_SECTOR']).filter((v) => !!v);
          const distinctSectors = [...new Set(allSectors)].sort();
          distinctSectors.push('Various');
          this.sectorFilterUniverse = distinctSectors;
          params.success(distinctSectors);
        } catch (e) {
          this.sectorFilterUniverse = [];
          params.success([]);
        }
      },
      valueFormatter: (params) => params.value ?? '',
      suppressSorting: true,
    };
  }

  private getDealStatusFilterValues() {
    return this.enumsByColumn.get('DEAL_STATUS') ?? [];
  }

  protected getLeadShortNameFilterParams(): ISetFilterParams {
    return {
      values: async (params) => {
        try {
          // Get static LEAD_MANAGER data
          const result = await this.connect.request('REQ_MUNI_LEAD_MANAGER_SHORTS', {});
          const mappedLeads: string[] = [];
          if (!result.REPLY.includes('MSG_NACK')) {
            mappedLeads.push(...(result.REPLY || []).map((r) => r['SHORT_NAME']).filter((v) => !!v));
          }

          // Get unmapped syndicate values from current grid data
          const unmappedSyndicateValues: Set<string> = new Set();
          if (params.api) {
            params.api.getModel().forEachNode((rowNode) => {
              const data = rowNode.data;
              if (data) {
                const shortName = data['SHORT_NAME'];
                const syndicate = data['SYNDICATE'];
                
                // If SHORT_NAME is empty/null and SYNDICATE exists, extract first value
                if (!shortName && syndicate) {
                  const firstSyndicateValue = syndicate.split(';')?.[0]?.trim();
                  if (firstSyndicateValue) {
                    unmappedSyndicateValues.add(firstSyndicateValue);
                  }
                }
              }
            });
          }

          // Combine mapped leads with unmapped syndicate values and sort alphabetically
          const allValues = [...new Set([...mappedLeads, ...Array.from(unmappedSyndicateValues)])];
          allValues.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          this.leadFilterUniverse = allValues;
          params.success(allValues);
        } catch (e) {
          this.leadFilterUniverse = [];
          params.success([]);
        }
      },
      valueFormatter: (params) => params.value ?? '',
      suppressSorting: true,
    };
  }

  async connectedCallback() {
    super.connectedCallback();
    this.enumValueMapping = await this.getEnumValueMappings();
    this.enumsByColumn = this.groupEnumsByColumn(this.enumValueMapping);
    await this.setRatingFilters();
    this.subscribeToSeriesRatings();
    this.onResizeBound = this.resizeGrid.bind(this);
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    window.addEventListener('resize', this.onResizeBound);
    document.addEventListener('click', this.handleDocumentClickBound);
    this.resizeGrid();
  }

  disconnectedCallback() {
    this.teardownSeriesRatingsStream();
    window.removeEventListener('resize', this.onResizeBound);
    document.removeEventListener('click', this.handleDocumentClickBound);
    super.disconnectedCallback();
  }

  private teardownSeriesRatingsStream(): void {
    if (this.seriesRatingsSubscription) {
      this.seriesRatingsSubscription.unsubscribe();
      this.seriesRatingsSubscription = null;
    }
  }

  private subscribeToSeriesRatings(): void {
    if (this.seriesRatingsSubscription) {
      return;
    }

    this.seriesRatingsSubscription = this.connect
      .stream(
        'ACTIVE_SERIES_RATINGS',
        (message: Message) => {
          if (message.MESSAGE_TYPE === 'LOGOFF_ACK' && message.LOGGED_OUT === true) {
            this.teardownSeriesRatingsStream();
            return;
          }
          void this.refreshSeriesRatingsMap();
        },
        (err) => {
          console.error('ACTIVE_SERIES_RATINGS stream error', err);
        },
        { MAX_ROWS: 10000, MAX_VIEW: 30000 },
      )
      .subscribe();

    void this.refreshSeriesRatingsMap();
  }

  private async refreshSeriesRatingsMap(): Promise<void> {
    try {
      const response = await this.connect.snapshot('ACTIVE_SERIES_RATINGS');
      this.seriesRatingsByDealId = buildSeriesRatingsByDealId(response.ROW ?? []);
      this.onExternalFilterChanged();
    } catch (error) {
      console.error('Failed to load series ratings for deal listing filter', error);
    }
  }

  private async setRatingFilters() {
    this.issuerCreditRatings = await this.getRatings();
  }

  private async getEnumValueMappings() {
    try {
      const result = await this.connect.request('REQ_ENUM_VALUE_MAPPING', {});
      if (result.REPLY.includes('MSG_NACK')) {
        throw new Error('Error retrieving filter options from server');
      }
      return result.REPLY;
    } catch (error) {
      throw new Error('Error retrieving filter options from server');
    }
  }


  private async getRatings(): Promise<IssuerRating> {
    try {
      const result = await this.connect.request('REQ_MUNI_REFERENCE_DATA', {
        REQUEST: {
          REF_DATA_TYPE: 'ISSUER_CREDIT_RATING',
        },
      });
      if (result.REPLY.includes('MSG_NACK')) {
        throw new Error('Error retrieving static data from server');
      }
      return result.REPLY[0]['ISSUER_RATINGS'] as IssuerRating;
    } catch (error) {
      throw new Error('Error retrieving static data from server');
    }
  }

  removeFilter(key) {
    const model = this.gridApi.getFilterModel();
    delete model[key];
    this.gridApi.setFilterModel(model);
  }

  handleRowClick(e: CustomEvent): void {
    const row = e.detail;
    this.$emit('dealClicked', row);
  }

  

  handleGridReady(e: CustomEvent): void {
    e.detail.event.columnApi.autoSizeColumn('dealStatus');
    this.gridApi = e.detail.event.api;
    this.colApi = e.detail.event.columnApi;
  }

  goToNextWeek(): void {
    this.setDealsDateValue(getNextMonday(this.dealsDate));
  }

  goToPreviousWeek(): void {
    if (this.forwardCalendar) {
      this.goToHome();
    } else {
      this.setDealsDateValue(getPreviousMonday(this.dealsDate));
    }
  }

  goToHome(): void {
    this.setDealsDateValue(getNearestMonday());
  }

  toggleForwardCalendar(): void {
    this.forwardCalendar = true;
    this.dealsDateCalendar = false;
    this.setCriteria();
  }

  toggleCalendar(): void {
    this.dealsDateCalendar = !this.dealsDateCalendar;
  }

  setDealsDate(e: CustomEvent): void {
    this.setDealsDateValue(e.detail);
  }

  private setCriteria(): void {
    if (this.forwardCalendar && this.featureFlags.isFeatureActivated(Features.ForwardCalendarAll)) {
      this.criteria = '';
    } else {
      this.criteria = getDealsDatasourceCriteria(this.dealsDate, this.forwardCalendar, this.openDetailViewDealIds);
    }
    this.onExternalFilterChanged();
  }

  openDetailViewDealIdsChanged(): void {
    this.setCriteria();
  }

  /**
   * Check if a deal belongs to the currently selected week.
   */
  private isDealInCurrentWeek(deal: { EXPECTED_PRICING_DATE?: string | number | null } | null | undefined): boolean {
    const expectedPricingDate = deal?.EXPECTED_PRICING_DATE;
    
    if (this.forwardCalendar) {
      // TBD deals (null expected pricing date) are included in Forward Calendar
      if (expectedPricingDate == null) {
        return true;
      }
      const min = getForwardCalendarMonday().getTime();
      return Number(expectedPricingDate) >= min;
    }
    
    if (!this.dealsDate) return false;

    // TBD deals (null expected pricing date) are excluded from current and historic weeks
    if (expectedPricingDate == null) {
      return false;
    }

    // Week boundaries in UTC to keep date-only fields consistent across timezones.
    const [mm, dd, yyyy] = this.dealsDate.split('-').map(Number);
    const mondayDate = new Date(Date.UTC(yyyy, (mm ?? 1) - 1, dd ?? 1));
    const monday = mondayDate.getTime();
    const fridayDate = new Date(mondayDate);
    fridayDate.setUTCDate(fridayDate.getUTCDate() + 4);
    const fridayEnd = fridayDate.getTime();

    const ts = Number(expectedPricingDate);
    return ts >= monday && ts <= fridayEnd;
  }

  /**
   * Notify grid that external filter has changed so it re-evaluates which rows to show.
   */
  private onExternalFilterChanged(): void {
    if (!this.gridApi) return;
    this.gridApi.onFilterChanged();
  }

  private resizeGrid(): void {
    clearTimeout(this.gridResizeTimeoutRef);
    this.gridResizeTimeoutRef = setTimeout(() => {
      if (!this.gridApi || !this.offsetWidth) return;
      this.gridApi.sizeColumnsToFit();
    }, 200);
  }

  clearAllFilters(): void {
    this.gridApi.setFilterModel({});
  }

  private handleDocumentClick(e): void {
    const path = e.composedPath();
    if (!path.includes(this.dateCalendar)) {
      this.dealsDateCalendar = false;
    }
  }

  private setDealsDateValue(date: string): void {
    if (checkIsForwardCalendar(date)) {
      this.forwardCalendar = true;
    } else {
      this.dealsDate = date;
      this.forwardCalendar = false;
    }
    this.dealsDateCalendar = false;
    this.setCriteria();
  }
}
